import React, { useState } from "react";

const ClaimFlow = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [claimStatus, setClaimStatus] = useState("idle"); // idle, processing, success, error

  const steps = [
    {
      number: "1️⃣",
      title: "Connect Wallet",
      description: "Secure connection to X1 EcoChain"
    },
    {
      number: "2️⃣",
      title: "Verify Identity",
      description: "One wallet = one claim. Real participants only."
    },
    {
      number: "3️⃣",
      title: "Confirm Smart Contract",
      description: "Green, gas-efficient transactions"
    },
    {
      number: "4️⃣",
      title: "Receive X1 Tokens",
      description: "Instantly in your wallet"
    }
  ];

  const handleClaim = () => {
    setClaimStatus("processing");
    // Simulate claim process
    setTimeout(() => {
      setClaimStatus("success");
      setCurrentStep(3);
    }, 3000);
  };

  const getButtonText = () => {
    switch (claimStatus) {
      case "processing": return "Verifying Transaction…";
      case "success": return "Claim Complete — Welcome to X1";
      case "error": return "Claim failed. Check network or try again.";
      default: return "Claim Now";
    }
  };

  return (
    <section id="claim" className="py-20 bg-gradient-dark relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-eco"></div>
      
      <div className="max-w-4xl mx-auto px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black gradient-text mb-4">
            Claim Your X1 Tokens
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Join the green blockchain revolution. Simple, secure, and sustainable.
          </p>
        </div>

        <div className="glass-card p-8 rounded-3xl mb-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`text-center relative ${
                  index <= currentStep ? 'opacity-100' : 'opacity-50'
                } transition-all duration-500`}
              >
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-1/2 w-full h-1 bg-gray-600 -z-10"></div>
                )}
                {index < currentStep && (
                  <div className="hidden md:block absolute top-8 left-1/2 w-full h-1 bg-gradient-eco -z-10"></div>
                )}

                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl mx-auto mb-4 transition-all duration-500 ${
                  index <= currentStep 
                    ? 'bg-gradient-eco text-eco-black scale-110' 
                    : 'bg-gray-700 text-gray-400'
                }`}>
                  {step.number}
                </div>
                <h3 className="font-bold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-gray-400">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card p-8 rounded-3xl text-center">
          {claimStatus === "success" ? (
            <div className="space-y-6">
              <div className="w-20 h-20 bg-gradient-eco rounded-2xl mx-auto flex items-center justify-center text-eco-black text-2xl">
                ⚪
              </div>
              <h3 className="text-2xl font-black gradient-text">Welcome to X1 EcoChain!</h3>
              <p className="text-gray-300 text-lg">
                You're early — and efficient. Your X1 Tokens are live on-chain.
                Next: stake, build, or spread the word.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
                <button className="btn-eco px-6 py-3 font-bold">
                  Stake Tokens
                </button>
                <button className="btn-secondary px-6 py-3 font-bold">
                  Share on Twitter
                </button>
              </div>
            </div>
          ) : claimStatus === "error" ? (
            <div className="space-y-6">
              <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl mx-auto flex items-center justify-center text-white text-2xl">
                ⚠️
              </div>
              <h3 className="text-2xl font-black text-red-400">Connection Issue</h3>
              <p className="text-gray-300 text-lg">
                Something broke in the chain. Reconnect your wallet or switch to the correct network.
              </p>
              <button 
                onClick={() => setClaimStatus("idle")}
                className="btn-eco px-6 py-3 font-bold"
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="w-32 h-32 mx-auto relative">
                <div className="absolute inset-0 bg-gradient-eco rounded-2xl opacity-20 animate-pulse"></div>
                <div className="absolute inset-4 bg-eco-black rounded-xl flex items-center justify-center">
                  <span className="text-4xl">🌍</span>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-2xl font-black gradient-text">Ready to Claim?</h3>
                <p className="text-gray-300">
                  X1 runs on Proof-of-Authority consensus — greener, faster, secure.
                </p>
              </div>

              <button
                onClick={handleClaim}
                disabled={claimStatus === "processing"}
                className={`w-full max-w-md mx-auto py-4 rounded-2xl font-bold text-lg transition-all duration-300 ${
                  claimStatus === "processing"
                    ? 'bg-gradient-to-r from-white to-gray-300 opacity-80 cursor-not-allowed'
                    : 'btn-eco hover:scale-105'
                }`}
              >
                {getButtonText()}
              </button>

              {claimStatus === "processing" && (
                <div className="pt-4">
                  <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
          <div className="glass-card p-6 rounded-2xl">
            <h4 className="font-bold text-white mb-3 flex items-center">
              <span className="w-6 h-6 bg-white rounded-full flex items-center justify-center text-eco-black text-sm mr-2">
                ℹ️
              </span>
              About Eligibility
            </h4>
            <p className="text-gray-300 text-sm">
              Your wallet must meet snapshot criteria or verified tasks. 
              Need help? Read how eligibility works →
            </p>
          </div>
          <div className="glass-card p-6 rounded-2xl">
            <h4 className="font-bold text-gray-300 mb-3 flex items-center">
              <span className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center text-eco-black text-sm mr-2">
                📊
              </span>
              Staking Benefits
            </h4>
            <p className="text-gray-300 text-sm">
              Staking keeps the network alive — and your tokens active. 
              Earn rewards while supporting green blockchain.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClaimFlow;